import axios, {
    type AxiosRequestConfig,
    type AxiosResponse,
} from 'axios'

import {
    normalizeApiError,
} from './normalize-api-error'

const apiBaseUrl =
    import.meta.env.VITE_API_BASE_URL

if (!apiBaseUrl) {
    throw new Error(
        'VITE_API_BASE_URL is not configured.',
    )
}

const axiosInstance = axios.create({
    baseURL: apiBaseUrl,

    /*
     * All TPM API calls must use the configured
     * API base URL.
     */
    allowAbsoluteUrls: false,

    /*
     * Required for ASP.NET Core Identity
     * authentication cookies.
     */
    withCredentials: true,

    /*
     * ASP.NET Core will expose the antiforgery
     * request token using this cookie name.
     */
    xsrfCookieName: 'XSRF-TOKEN',

    /*
     * Axios sends the token using this header.
     * ASP.NET Core antiforgery will be configured
     * to expect the same header.
     */
    xsrfHeaderName: 'X-XSRF-TOKEN',

    /*
     * Send the XSRF token for state-changing
     * requests, including cross-origin requests
     * when applicable.
     */
    withXSRFToken: (config) => {
        const method =
            config.method?.toUpperCase()

        return (
            method !== 'GET' &&
            method !== 'HEAD' &&
            method !== 'OPTIONS'
        )
    },

    // timeout: 30_000,

    headers: {
        Accept: 'application/json',
    },
})

axiosInstance.interceptors.response.use(
    (response) => response,

    (error: unknown) => {
        if (axios.isCancel(error)) {
            return Promise.reject(error)
        }

        return Promise.reject(
            normalizeApiError(error),
        )
    },
)

async function request<
    TResponse,
    TRequest = unknown,
>(
    config: AxiosRequestConfig<TRequest>,
): Promise<TResponse> {
    const response =
        await axiosInstance.request<
            TResponse,
            AxiosResponse<TResponse>,
            TRequest
        >(config)

    return response.data
}

function get<TResponse>(
    url: string,
    options?: AxiosRequestConfig,
): Promise<TResponse> {
    return request<TResponse>({
        ...options,
        method: 'GET',
        url,
    })
}

function post<
    TResponse,
    TRequest = unknown,
>(
    url: string,
    data?: TRequest,
    options?: AxiosRequestConfig<TRequest>,
): Promise<TResponse> {
    return request<
        TResponse,
        TRequest
    >({
        ...options,
        method: 'POST',
        url,
        data,
    })
}

function put<
    TResponse,
    TRequest = unknown,
>(
    url: string,
    data?: TRequest,
    options?: AxiosRequestConfig<TRequest>,
): Promise<TResponse> {
    return request<
        TResponse,
        TRequest
    >({
        ...options,
        method: 'PUT',
        url,
        data,
    })
}

function patch<
    TResponse,
    TRequest = unknown,
>(
    url: string,
    data?: TRequest,
    options?: AxiosRequestConfig<TRequest>,
): Promise<TResponse> {
    return request<
        TResponse,
        TRequest
    >({
        ...options,
        method: 'PATCH',
        url,
        data,
    })
}

function remove<
    TResponse,
    TRequest = unknown,
>(
    url: string,
    data?: TRequest,
    options?: AxiosRequestConfig<TRequest>,
): Promise<TResponse> {
    return request<
        TResponse,
        TRequest
    >({
        ...options,
        method: 'DELETE',
        url,
        data,
    })
}

export const apiClient = {
    get,
    post,
    put,
    patch,
    delete: remove,
}